import { useState } from "react";
import { sendContact } from "../lib/api";
import Seo from "../components/Seo";
import { toast } from "sonner";
import { Mail, Phone, MapPin, ArrowRight, MessageCircle, Instagram, Twitter, Linkedin, Facebook, Youtube } from "lucide-react";

const COUNTRIES = ["UAE", "Saudi Arabia", "Qatar", "Bahrain", "Oman", "Kuwait", "Nigeria", "Ghana", "Kenya", "South Africa", "Egypt", "Other"];

const SOCIALS = [
  { Icon: Instagram, label: "Instagram", handle: "@JoinDealVest", url: "https://instagram.com/JoinDealVest" },
  { Icon: ({ size }) => (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
    ), label: "TikTok", handle: "@JoinDealVest", url: "https://tiktok.com/@JoinDealVest" },
  { Icon: Twitter, label: "X (Twitter)", handle: "@JoinDealVest", url: "https://x.com/JoinDealVest" },
  { Icon: Linkedin, label: "LinkedIn", handle: "@JoinDealVest", url: "https://linkedin.com/company/JoinDealVest" },
  { Icon: Facebook, label: "Facebook", handle: "@JoinDealVest", url: "https://facebook.com/JoinDealVest" },
  { Icon: Youtube, label: "YouTube", handle: "@JoinDealVest", url: "https://youtube.com/@JoinDealVest" },
];

export default function ContactPage() {
  const [f, setF] = useState({ name: "", email: "", phone: "", country: "UAE", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await sendContact(f); toast.success("Message received. We'll respond within 24 hours."); setF({ name: "", email: "", phone: "", country: "UAE", subject: "", message: "" }); }
    catch { toast.error("Failed to send. Try again."); }
    setBusy(false);
  };

  return (
    <div data-testid="contact-page" className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
      <Seo
        title="Contact DealVest — Email, Phone, WhatsApp & Social"
        description="Talk to DealVest about buying, selling, raising capital, or partnerships. Reach us by email, phone, WhatsApp or on social — we respond within 24 hours."
        path="/contact"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Contact DealVest",
          "url": "https://joindealvest.com/contact"
        }}
      />

      <div className="grid lg:grid-cols-2 gap-12">
        <div>
          <span className="dv-eyebrow">Contact</span>
          <h1 className="dv-display text-4xl md:text-6xl leading-tight my-4">Let's build something great.</h1>
          <p className="text-slate-600 leading-relaxed text-lg mb-8">Whether you're buying, selling, raising or exploring partnerships — we respond within 24 hours.</p>

          <div className="space-y-3 mb-10">
            <a href="mailto:hello@dealvest.com" className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors" data-testid="contact-email-link">
              <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center"><Mail size={18} className="text-blue-600" /></div>
              <div><div className="text-xs text-slate-500 uppercase tracking-wider font-medium">Email</div><div className="text-slate-900 font-semibold">hello@dealvest.com</div></div>
            </a>
            <a href="tel:+97140000000" className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors" data-testid="contact-phone-link">
              <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center"><Phone size={18} className="text-blue-600" /></div>
              <div><div className="text-xs text-slate-500 uppercase tracking-wider font-medium">Phone</div><div className="text-slate-900 font-semibold">+971 (0) 4 000 0000</div></div>
            </a>
            <a href="https://wa.me/97140000000" target="_blank" rel="noreferrer" className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors" data-testid="contact-whatsapp-link">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center"><MessageCircle size={18} className="text-emerald-600" /></div>
              <div><div className="text-xs text-slate-500 uppercase tracking-wider font-medium">WhatsApp</div><div className="text-slate-900 font-semibold">Chat instantly</div></div>
            </a>
            <div className="flex items-center gap-3 p-3 rounded-xl">
              <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center"><MapPin size={18} className="text-blue-600" /></div>
              <div><div className="text-xs text-slate-500 uppercase tracking-wider font-medium">Office</div><div className="text-slate-900 font-semibold">DIFC, Dubai — UAE</div></div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-slate-900 mb-3">Follow us</h3>
            <div className="grid grid-cols-2 gap-2">
              {SOCIALS.map(({ Icon, label, handle, url }) => (
                <a key={label} href={url} target="_blank" rel="noopener noreferrer" data-testid={`contact-social-${label.toLowerCase().replace(/[^a-z]/g, "")}`}
                  className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition-colors">
                  <Icon size={18} className="text-slate-700" />
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{label}</div>
                    <div className="text-xs text-slate-500">{handle}</div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 dv-shadow-soft self-start" data-testid="contact-form">
          <h3 className="font-bold text-xl mb-5">Send a message</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><label className="dv-label">Name</label><input className="dv-input" required value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="contact-form-name" /></div>
            <div><label className="dv-label">Email</label><input type="email" className="dv-input" required value={f.email} onChange={e => setF({ ...f, email: e.target.value })} data-testid="contact-form-email" /></div>
            <div><label className="dv-label">Phone</label><input className="dv-input" value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} /></div>
            <div><label className="dv-label">Country</label><select className="dv-input" value={f.country} onChange={e => setF({ ...f, country: e.target.value })}>{COUNTRIES.map(c => <option key={c}>{c}</option>)}</select></div>
            <div><label className="dv-label">Subject</label><input className="dv-input" required value={f.subject} onChange={e => setF({ ...f, subject: e.target.value })} /></div>
            <div className="col-span-2"><label className="dv-label">Message</label><textarea rows="5" className="dv-input is-textarea" required value={f.message} onChange={e => setF({ ...f, message: e.target.value })} data-testid="contact-form-message" /></div>
            <div className="col-span-2"><button type="submit" disabled={busy} className="dv-btn dv-btn-primary w-full" data-testid="contact-form-submit">{busy ? "Sending..." : "Send Message"} <ArrowRight size={16} /></button></div>
          </div>
        </form>
      </div>
    </div>
  );
}
